import { api, ApiError } from '@/api/client';
import type {
  ChangePasswordRequest,
  LoginRequest,
  StepupRequest,
  StepupResponse,
  User,
} from '@/api/types';

/**
 * Typed wrappers over the Mission Control auth endpoints. These map 1:1
 * to §4.1 of the plan and the REST surface in §5. Every call uses the
 * shared `api` helper so CSRF origin handling, error parsing, and
 * tracing are centralized.
 */
export const authApi = {
  /** POST /api/auth/login — opaque session cookie issued on success. */
  login: async (req: LoginRequest): Promise<User> => {
    const res = await api.post<{ user: User; must_change_password: boolean }>('/api/auth/login', req);
    return { ...res.user, must_change_password: res.must_change_password };
  },

  /** POST /api/auth/logout — invalidates the current session. */
  logout: () => api.post<void>('/api/auth/logout'),

  /** POST /api/auth/logout-all — invalidates every session for the user. */
  logoutAll: () => api.post<void>('/api/auth/logout-all'),

  /**
   * GET /api/auth/me — returns the current user, or `null` if the backend
   * returns 401 (not logged in). Other errors are re-thrown.
   */
  me: async (): Promise<User | null> => {
    try {
      return await api.get<User>('/api/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },

  /** POST /api/auth/change-password — backend returns {ok: true}, not User. */
  changePassword: (req: ChangePasswordRequest) =>
    api.post<{ ok: boolean }>('/api/auth/change-password', req),

  /** POST /api/auth/stepup — returns a 5-minute elevation window. */
  stepup: (req: StepupRequest) =>
    api.post<StepupResponse>('/api/auth/stepup', req),

  // Sessions listing and per-session revocation removed — backend does
  // not implement GET /api/auth/sessions or DELETE /api/auth/sessions/{id}.
  // Use logoutAll() to revoke all sessions instead.
};
